import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { FaBookOpen, FaPencilAlt, FaTrash } from "react-icons/fa";
import StorytellingPhonePreview from "./StorytellingPhonePreview";
import StorytellingTypographySection from "./StorytellingTypographySection";
import StorytellingNameDisplaySection from "./StorytellingNameDisplaySection";
import StorytellingQuestionField from "./StorytellingQuestionField";
import StorytellingChapterTitleField from "./StorytellingChapterTitleField";
import StorytellingHeaderMetaSection from "./StorytellingHeaderMetaSection";
import {
  getStorytellingFontOptions,
  normalizeStorytellingDisplay,
  getEffectiveStorytellingDisplay,
  cloneStorytellingDisplay,
  normalizeStorytellingList,
  storytellingHasContent,
  STORYTELLING_BACKGROUND_STYLES,
} from "./storytellingUtils";
import { resolveStorytellingNameParts } from "./storytellingPresentation";
import { normalizeStorytellingHeaderMeta } from "./storytellingHeaderMeta";
import { STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS } from "./storytellingFonts";
import "../../../../assets/css/start-now-wizard.css";
import "./StorytellingStepper.css";

const STEPS = [
  { key: "content", labelKey: "storytellingStepContent" },
  { key: "style", labelKey: "storytellingStepStyle" },
  { key: "confirm", labelKey: "storytellingStepConfirm" },
];

function StorytellingStepper({
  restaurantData,
  setRestaurantData,
  previewStorytellingImages,
  uploadsServer,
  logoUrl,
  menuButtonColor,
  updateStorytellingField,
  addStorytellingEntry,
  removeStorytellingEntry,
  onStorytellingImageChange,
  onDeleteStorytellingImage,
  onSubmit,
  isSubmitting = false,
}) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const lastCustomDisplayRef = useRef(null);

  const persistCustomSnapshot = (displayState) => {
    lastCustomDisplayRef.current = cloneStorytellingDisplay(displayState);
  };

  const stories = useMemo(
    () => normalizeStorytellingList(restaurantData?.storytelling),
    [restaurantData?.storytelling]
  );

  const display = useMemo(
    () =>
      normalizeStorytellingDisplay(
        restaurantData?.storytellingDisplay,
        menuButtonColor
      ),
    [restaurantData?.storytellingDisplay, menuButtonColor]
  );

  const previewDisplay = useMemo(
    () =>
      getEffectiveStorytellingDisplay(
        restaurantData?.storytellingDisplay,
        menuButtonColor
      ),
    [restaurantData?.storytellingDisplay, menuButtonColor]
  );

  useEffect(() => {
    if (!restaurantData?.storytellingDisplay) return;
    const normalized = normalizeStorytellingDisplay(
      restaurantData.storytellingDisplay,
      menuButtonColor
    );
    if (normalized.useCustomDesign) {
      persistCustomSnapshot(normalized);
    }
  }, [restaurantData?.storytellingDisplay, menuButtonColor]);

  const updateDisplay = (field, value) => {
    setRestaurantData((prev) => {
      const nextDisplay = {
        ...normalizeStorytellingDisplay(prev?.storytellingDisplay, menuButtonColor),
        [field]: value,
      };
      if (nextDisplay.useCustomDesign) {
        persistCustomSnapshot(nextDisplay);
      }
      return {
        ...prev,
        storytellingDisplay: nextDisplay,
      };
    });
  };

  const applyDefaultDesign = () => {
    setRestaurantData((prev) => {
      const current = normalizeStorytellingDisplay(
        prev?.storytellingDisplay,
        menuButtonColor
      );
      if (current.useCustomDesign) {
        persistCustomSnapshot(current);
      }
      return {
        ...prev,
        storytellingDisplay: {
          ...current,
          useCustomDesign: false,
        },
      };
    });
  };

  const applyCustomDesign = () => {
    setRestaurantData((prev) => {
      if (lastCustomDisplayRef.current) {
        return {
          ...prev,
          storytellingDisplay: {
            ...cloneStorytellingDisplay(lastCustomDisplayRef.current),
            useCustomDesign: true,
          },
        };
      }
      const current = normalizeStorytellingDisplay(
        prev?.storytellingDisplay,
        menuButtonColor
      );
      return {
        ...prev,
        storytellingDisplay: {
          ...current,
          useCustomDesign: true,
        },
      };
    });
  };

  const headerMeta = useMemo(
    () =>
      normalizeStorytellingHeaderMeta(
        display.headerMeta,
        restaurantData || {},
        t
      ),
    [display.headerMeta, restaurantData, t]
  );

  const previewHeaderMeta = useMemo(
    () =>
      normalizeStorytellingHeaderMeta(
        previewDisplay.headerMeta,
        restaurantData || {},
        t
      ),
    [previewDisplay.headerMeta, restaurantData, t]
  );

  const updateHeaderMeta = (key, field, value) => {
    setRestaurantData((prev) => {
      const currentDisplay = normalizeStorytellingDisplay(
        prev?.storytellingDisplay,
        menuButtonColor
      );
      const nextDisplay = {
        ...currentDisplay,
        useCustomDesign: true,
        headerMeta: {
          ...currentDisplay.headerMeta,
          [key]: {
            ...currentDisplay.headerMeta[key],
            [field]: value,
          },
        },
      };
      persistCustomSnapshot(nextDisplay);
      return {
        ...prev,
        storytellingDisplay: nextDisplay,
      };
    });
  };

  const stepComplete = useMemo(() => {
    const hasContent = storytellingHasContent(restaurantData?.storytelling);
    return [hasContent, hasContent, hasContent];
  }, [restaurantData?.storytelling]);

  const doneUpToIndex = stepComplete.lastIndexOf(true);
  const pct =
    STEPS.length <= 1 || doneUpToIndex < 0
      ? 0
      : (Math.min(activeStep, doneUpToIndex) / (STEPS.length - 1)) * 100;

  const goNext = () => {
    if (activeStep === 0 && !storytellingHasContent(restaurantData?.storytelling)) {
      Swal.fire({
        icon: "warning",
        title: t("storytellingStep1Required"),
        confirmButtonColor: "#FA8072",
      });
      return;
    }
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const backgroundOptions = [
    { value: "warm", label: t("storytellingBgWarm") },
    { value: "light", label: t("storytellingBgLight") },
    { value: "neutral", label: t("storytellingBgNeutral") },
  ];

  const fontOptions = useMemo(() => getStorytellingFontOptions(t), [t]);

  const sizeOptions = [
    { value: "small", label: t("storytellingNameSizeSmall") },
    { value: "medium", label: t("storytellingNameSizeMedium") },
    { value: "large", label: t("storytellingNameSizeLarge") },
  ];

  const renderTypographySummary = (role, titleKey, skipColor = false) => {
    const familyField = `${role}FontFamily`;
    const sizeField = `${role}FontSize`;
    const colorField = `${role}Color`;
    const defaultColor = STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS[role];

    return (
      <>
        <li>
          <span>{t(titleKey)} — {t("storytellingTypographyFontLabel")}</span>
          <strong>
            {fontOptions.find((o) => o.value === display[familyField])?.label}
          </strong>
        </li>
        <li>
          <span>{t(titleKey)} — {t("storytellingTypographySizeLabel")}</span>
          <strong>
            {sizeOptions.find((o) => o.value === display[sizeField])?.label}
          </strong>
        </li>
        {!skipColor ? (
          <li>
            <span>{t(titleKey)} — {t("storytellingTypographyColorLabel")}</span>
            <strong className="storytelling-stepper__color-swatch">
              <span style={{ background: display[colorField] || defaultColor }} />
              {display[colorField] || defaultColor}
            </strong>
          </li>
        ) : null}
      </>
    );
  };

  const resolvedNameParts = useMemo(
    () => resolveStorytellingNameParts(restaurantData?.nameRes, previewDisplay),
    [restaurantData?.nameRes, previewDisplay]
  );

  return (
    <div className="storytelling-stepper">
      <div className="storytelling-stepper__header">
        <span className="storytelling-section__icon" aria-hidden="true">
          <FaBookOpen />
        </span>
        <div>
          <h3 className="storytelling-section__title">{t("storytelling")}</h3>
          <p className="storytelling-section__subtitle mb-0">
            {t("storytellingStepperIntro")}
          </p>
        </div>
      </div>

      <div className="wizard-stepper storytelling-stepper__wizard">
        <div
          className="wizard-stepper-line"
          style={{
            background: `linear-gradient(to right, #f47f72 0%, #f47f72 ${pct}%, #d7dce3 ${pct}%, #d7dce3 100%)`,
          }}
        />
        <div className="wizard-stepper-track">
          {STEPS.map((step, idx) => {
            const isActive = idx === activeStep;
            const isComplete = stepComplete[idx] && idx < activeStep;
            const border = isActive || isComplete ? "#f47f72" : "#9ea5b2";
            const bg = isActive || isComplete ? "#f47f72" : "transparent";
            const color = isActive || isComplete ? "#fff" : "#0f1320";

            return (
              <button
                key={step.key}
                type="button"
                onClick={() => {
                  if (idx > 0 && !storytellingHasContent(restaurantData?.storytelling)) {
                    Swal.fire({
                      icon: "warning",
                      title: t("storytellingStep1Required"),
                      confirmButtonColor: "#FA8072",
                    });
                    return;
                  }
                  setActiveStep(idx);
                }}
                className="wizard-step-item"
                title={t(step.labelKey)}
              >
                <div
                  className="wizard-step-circle"
                  style={{ borderColor: border, background: bg, color }}
                >
                  {isComplete ? <i className="fa fa-check" /> : idx + 1}
                </div>
                <div
                  className={`wizard-step-label ${isActive ? "active" : ""} ${
                    isComplete ? "done" : ""
                  }`}
                >
                  {t(step.labelKey)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="storytelling-stepper__body">
        {activeStep === 0 && (
          <div className="storytelling-stepper__step storytelling-stepper__step--content">
            <p className="storytelling-stepper__step-hint">{t("storytellingInfo")}</p>

            {stories.map((entry, index) => (
              <div className="storytelling-entry-card" key={`storytelling-${index}`}>
                <div className="storytelling-entry-card__header">
                  <strong>{t("storytellingEntry", { number: index + 1 })}</strong>
                  {stories.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeStorytellingEntry(index)}
                    >
                      <FaTrash size={12} style={{ marginRight: 6 }} />
                      {t("removeStorytelling")}
                    </button>
                  )}
                </div>

                <div className="storytelling-entry-card__body">
                  <div className="storytelling-entry-card__fields">
                    <StorytellingChapterTitleField
                      index={index}
                      chapterLabel={entry.chapterLabel}
                      updateStorytellingField={updateStorytellingField}
                    />

                    <StorytellingQuestionField
                      index={index}
                      question={entry.question}
                      updateStorytellingField={updateStorytellingField}
                    />

                    <div className="storytelling-entry-card__description-wrap">
                      <label className="form-label text-primary">
                        {t("storytellingYourAnswer")}
                      </label>
                      <textarea
                        className="form-control storytelling-entry-card__description"
                        placeholder={t("storytellingDescriptionPlaceholder")}
                        value={entry.description || ""}
                        onChange={(e) =>
                          updateStorytellingField(index, "description", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="storytelling-entry-card__media">
                    <label className="form-label text-primary">
                      {t("storytellingImage")}
                    </label>
                    <div className="storytelling-entry-card__image-shell">
                      {previewStorytellingImages[index] ? (
                        <img
                          src={previewStorytellingImages[index]}
                          alt="Storytelling preview"
                          className="storytelling-entry-card__image storytelling-entry-card__image--preview"
                        />
                      ) : entry.image ? (
                        <img
                          src={`${uploadsServer}/restaurant/${entry.image}`}
                          alt="Storytelling"
                          className="storytelling-entry-card__image"
                        />
                      ) : (
                        <div className="storytelling-entry-card__image-placeholder">
                          {t("noStorytellingImage")}
                        </div>
                      )}

                      <input
                        id={`storytelling-image-upload-${index}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => onStorytellingImageChange(index, e)}
                        style={{ display: "none" }}
                      />

                      <div className="storytelling-entry-card__media-actions">
                        <label
                          htmlFor={`storytelling-image-upload-${index}`}
                          className="storytelling-entry-card__action-btn storytelling-entry-card__action-btn--edit"
                        >
                          <FaPencilAlt size={15} />
                        </label>

                        {(entry.image || previewStorytellingImages[index]) && (
                          <button
                            type="button"
                            className="storytelling-entry-card__action-btn storytelling-entry-card__action-btn--delete"
                            onClick={() => onDeleteStorytellingImage(index)}
                          >
                            <FaTrash size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-outline-primary storytelling-add-btn"
              onClick={addStorytellingEntry}
            >
              <i className="fas fa-plus" style={{ marginRight: 8 }} />
              {t("addStorytelling")}
            </button>
          </div>
        )}

        {activeStep === 1 && (
          <div className="storytelling-stepper__step storytelling-stepper__step--style">
            <div className="storytelling-stepper__customize-grid">
              <div className="storytelling-stepper__controls">
                <h4 className="text-primary mb-3">{t("storytellingStyleTitle")}</h4>
                <p className="text-muted storytelling-stepper__step-hint">
                  {t("storytellingStyleHint")}
                </p>

                <div className="storytelling-stepper__control-section">
                  <div className="storytelling-stepper__option-group storytelling-stepper__design-mode">
                    <button
                      type="button"
                      className={`storytelling-stepper__option-btn${
                        !display.useCustomDesign
                          ? " storytelling-stepper__option-btn--active"
                          : ""
                      }`}
                      onClick={applyDefaultDesign}
                    >
                      {t("storytellingDesignDefault")}
                    </button>
                    <button
                      type="button"
                      className={`storytelling-stepper__option-btn${
                        display.useCustomDesign
                          ? " storytelling-stepper__option-btn--active"
                          : ""
                      }`}
                      onClick={applyCustomDesign}
                    >
                      {t("storytellingDesignCustom")}
                    </button>
                  </div>

                  {!display.useCustomDesign ? (
                    <p className="text-muted small mb-0 mt-3">
                      {t("storytellingStyleDefaultHint")}
                    </p>
                  ) : null}
                </div>

                {display.useCustomDesign ? (
                  <>
                <div className="storytelling-stepper__control-section storytelling-stepper__control-section--appearance">
                  <h5 className="storytelling-stepper__control-title">
                    {t("storytellingAppearanceLabel")}
                  </h5>

                  <div className="mb-3">
                    <label className="form-label text-primary" htmlFor="storytelling-accent">
                      {t("storytellingAccentColorLabel")}
                    </label>
                    <div className="storytelling-stepper__color-row">
                      <input
                        id="storytelling-accent"
                        type="color"
                        value={display.accentColor || menuButtonColor || "#c1a37a"}
                        onChange={(e) => updateDisplay("accentColor", e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => updateDisplay("accentColor", menuButtonColor || "#c1a37a")}
                      >
                        {t("storytellingResetAccent")}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("storytellingBackgroundLabel")}
                    </label>
                    <div className="storytelling-stepper__option-group">
                      {backgroundOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`storytelling-stepper__option-btn${
                            display.backgroundStyle === opt.value
                              ? " storytelling-stepper__option-btn--active"
                              : ""
                          }`}
                          onClick={() => updateDisplay("backgroundStyle", opt.value)}
                        >
                          <span
                            className="storytelling-stepper__bg-swatch"
                            style={{ background: STORYTELLING_BACKGROUND_STYLES[opt.value] }}
                            aria-hidden="true"
                          />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="storytelling-show-numbers"
                      checked={display.showEntryNumbers}
                      onChange={(e) =>
                        updateDisplay("showEntryNumbers", e.target.checked)
                      }
                    />
                    <label className="form-check-label" htmlFor="storytelling-show-numbers">
                      {t("storytellingShowNumbers")}
                    </label>
                  </div>
                </div>

                <div className="storytelling-stepper__control-section">
                  <StorytellingHeaderMetaSection
                    rawHeaderMeta={display.headerMeta}
                    updateHeaderMeta={updateHeaderMeta}
                    restaurantData={restaurantData}
                  />
                </div>

                <div className="storytelling-stepper__control-section">
                  <h5 className="storytelling-stepper__control-title">
                    {t("storytellingTypographySectionLabel")}
                  </h5>

                <StorytellingTypographySection
                  title={t("storytellingNameStyleLabel")}
                  role="name"
                  display={display}
                  updateDisplay={updateDisplay}
                  fontOptions={fontOptions}
                  sizeOptions={sizeOptions}
                  defaultColor={STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.name}
                  hideColor
                  t={t}
                />

                <StorytellingNameDisplaySection
                  restaurantName={restaurantData?.nameRes}
                  display={display}
                  updateDisplay={updateDisplay}
                  menuButtonColor={menuButtonColor}
                  t={t}
                />

                <StorytellingTypographySection
                  title={t("storytellingQuestionStyleLabel")}
                  role="question"
                  display={display}
                  updateDisplay={updateDisplay}
                  fontOptions={fontOptions}
                  sizeOptions={sizeOptions}
                  defaultColor={STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.question}
                  t={t}
                />

                <StorytellingTypographySection
                  title={t("storytellingDescriptionStyleLabel")}
                  role="description"
                  display={display}
                  updateDisplay={updateDisplay}
                  fontOptions={fontOptions}
                  sizeOptions={sizeOptions}
                  defaultColor={STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.description}
                  t={t}
                />
                </div>
                  </>
                ) : null}
              </div>

              <div
                className={[
                  "storytelling-stepper__preview-wrap",
                  !display.useCustomDesign
                    ? "storytelling-stepper__preview-wrap--compact"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <StorytellingPhonePreview
                  restaurantName={restaurantData?.nameRes}
                  address={restaurantData?.address}
                  city={restaurantData?.city}
                  serviceType={restaurantData?.serviceType}
                  stories={stories}
                  display={previewDisplay}
                  headerMeta={previewHeaderMeta}
                  menuButtonColor={menuButtonColor}
                  previewImages={previewStorytellingImages}
                  uploadsServer={uploadsServer}
                  compact={!display.useCustomDesign}
                />
              </div>
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div className="storytelling-stepper__step storytelling-stepper__step--confirm">
            <h4 className="text-primary mb-2">{t("storytellingConfirmTitle")}</h4>
            <p className="text-muted storytelling-stepper__step-hint mb-4">
              {t("storytellingConfirmHint")}
            </p>

            <div className="storytelling-stepper__summary-block">
              <h5>{t("storytellingStepContent")}</h5>
              <p className="text-muted mb-3">
                {t("storytellingConfirmStoriesCount", { count: stories.length })}
              </p>
              <div className="storytelling-stepper__summary-stories">
                {stories.map((entry, index) => (
                  <div className="storytelling-stepper__summary-card" key={`summary-${index}`}>
                    <strong>{entry.question || t("storytellingQuestion")}</strong>
                    <p className="mb-0 text-muted">
                      {entry.description || t("storytellingDescription")}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="storytelling-stepper__summary-block">
              <h5>{t("storytellingStepStyle")}</h5>
              <ul className="storytelling-stepper__summary-list">
                <li>
                  <span>{t("storytellingStyleModeLabel")}</span>
                  <strong>
                    {display.useCustomDesign
                      ? t("storytellingDesignCustom")
                      : t("storytellingDesignDefault")}
                  </strong>
                </li>
                {display.useCustomDesign ? (
                  <>
                <li>
                  <span>{t("storytellingBackgroundLabel")}</span>
                  <strong>
                    {
                      backgroundOptions.find((o) => o.value === previewDisplay.backgroundStyle)
                        ?.label
                    }
                  </strong>
                </li>
                <li>
                  <span>{t("storytellingAccentColorLabel")}</span>
                  <strong className="storytelling-stepper__color-swatch">
                    <span
                      style={{
                        background: previewDisplay.accentColor || menuButtonColor || "#c1a37a",
                      }}
                    />
                    {previewDisplay.accentColor || menuButtonColor || "#c1a37a"}
                  </strong>
                </li>
                <li>
                  <span>{t("storytellingShowNumbers")}</span>
                  <strong>
                    {previewDisplay.showEntryNumbers ? t("yes") : t("no")}
                  </strong>
                </li>
                {renderTypographySummary("name", "storytellingNameStyleLabel", true)}
                <li>
                  <span>{t("storytellingNameDisplayLabel")}</span>
                  <strong>
                    {resolvedNameParts.primary}
                    {resolvedNameParts.accent}
                  </strong>
                </li>
                <li>
                  <span>{t("storytellingNamePrimaryColorLabel")}</span>
                  <strong className="storytelling-stepper__color-swatch">
                    <span
                      style={{
                        background:
                          previewDisplay.nameColor || STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.name,
                      }}
                    />
                    {previewDisplay.nameColor || STORYTELLING_TYPOGRAPHY_DEFAULT_COLORS.name}
                  </strong>
                </li>
                <li>
                  <span>{t("storytellingNameAccentColorLabel")}</span>
                  <strong className="storytelling-stepper__color-swatch">
                    <span
                      style={{
                        background:
                          previewDisplay.nameAccentColor ||
                          previewDisplay.accentColor ||
                          menuButtonColor ||
                          "#c1a37a",
                      }}
                    />
                    {previewDisplay.nameAccentColor ||
                      previewDisplay.accentColor ||
                      menuButtonColor ||
                      "#c1a37a"}
                  </strong>
                </li>
                {renderTypographySummary("question", "storytellingQuestionStyleLabel")}
                {renderTypographySummary("description", "storytellingDescriptionStyleLabel")}
                  </>
                ) : null}
              </ul>
            </div>

            <div className="storytelling-stepper__confirm-preview">
              <StorytellingPhonePreview
                restaurantName={restaurantData?.nameRes}
                address={restaurantData?.address}
                city={restaurantData?.city}
                serviceType={restaurantData?.serviceType}
                stories={stories}
                display={previewDisplay}
                headerMeta={previewHeaderMeta}
                menuButtonColor={menuButtonColor}
                previewImages={previewStorytellingImages}
                uploadsServer={uploadsServer}
              />
            </div>
          </div>
        )}
      </div>

      <div className="storytelling-stepper__footer">
        {activeStep > 0 ? (
          <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
            {t("previous")}
          </button>
        ) : (
          <span />
        )}

        {activeStep < STEPS.length - 1 ? (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            {t("next")}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("wizardConfirmSubmitting") : t("submitChanges")}
          </button>
        )}
      </div>
    </div>
  );
}

export default StorytellingStepper;
